/* eslint-disable camelcase */
import type { GenericAbortSignal } from 'axios';
import qs from 'query-string';
import { doorClient } from '.';
import type { ExternalType, Long } from './tradeV2';

/**
 * 物流查询 V2 接口类型。
 * 对应后端 TypeSpec: LogisticsV2 / GET /trade/v2/logistics
 * 数据模型对应 leyan-proto 1.45.7: overseaim_trade_v2.proto
 */

export type LeChatTraceStatus =
  | 'LE_CHAT_UNKNOWN_TRACE'
  | 'LE_CHAT_NOT_START'
  | 'LE_CHAT_WAITING_COLLECT'
  | 'LE_CHAT_WAITING_SEND'
  | 'LE_CHAT_WAITING_SIGN'
  | 'LE_CHAT_SIGNED'
  | 'LE_CHAT_SEND_FAIL'
  | 'LE_CHAT_RETURN_FAIL_WAIT_SELLER_PICK'
  | 'LE_CHAT_RETURN_FAIL_SCRAPPED'
  | 'LE_CHAT_PACKAGE_LOST'
  | 'LE_CHAT_PACKAGE_SCRAPPED'
  | 'LE_CHAT_PACKAGE_RETURN_SUCCESS'
  | 'LE_CHAT_TRADE_CLOSED';

export type LogisticsDataSection =
  | 'SECTION_UNSPECIFIED'
  | 'SHIPMENTS'
  | 'ERP_SHIPMENTS'
  | 'REVERSE_SHIPMENTS'
  | 'TRACKING_NODES'
  | 'TIMELINE';

export type ErpShipmentErpType =
  | 'ERP_TYPE_UNSPECIFIED'
  | 'LAZBAO'
  | 'HISENSE'
  | 'QIANYI'
  | 'JUSHUITAN'
  | 'OTHER';

export interface GetLogisticsV2Query {
  internalTradeIds?: string[];
  externalTradeIds?: string[];
  internalStoreId?: Long;
  cacheMaxAgeSeconds?: number;
  include?: LogisticsDataSection[];
  orgId?: string;
  userId?: Long;
}

export interface ApiResponse<T> {
  code: number;
  msg: string;
  data?: T;
}

export interface GetLogisticsV2Data {
  logisticsData?: LogisticsData[];
  includedSections?: LogisticsDataSection[];
  notFoundTradeIds?: string[];
}

export interface LogisticsData {
  internalTradeId?: Long;
  externalTradeId?: string;
  platform?: ExternalType;
  region?: string;
  deliverySlaTimeMs?: Long;
  latestNode?: LogisticsNode;
  shipments?: Shipment[];
  erpShipments?: ErpShipment[];
  reverseShipments?: ReverseShipment[];
}

export interface Shipment {
  packageId?: string;
  trackingNumber?: string;
  isBookingShipment?: boolean;
  shipmentId?: string;
  shippingCarrier?: string;
  warehouseCode?: string;
  firstMileTrackingNumber?: string;
  lastMileTrackingNumber?: string;
  plpNumber?: string;
  logisticsMode?: string;
  orderLineIds?: string[];
  leChatTraceStatus?: LeChatTraceStatus;
  leChatTraceStatusOriginal?: string;
  platformLeChatTraceStatus?: LeChatTraceStatus;
  platformLeChatTraceStatusOriginal?: string;
  trackingNodes?: LogisticsNode[];
  timeline?: LogisticsNode[];
  estimatedDeliveryLimitTimeMs?: Long;
}

export interface ErpShipment {
  erpType?: ErpShipmentErpType;
  erpTypeOriginal?: string;
  trackingNumber?: string;
  logisticsNo?: string;
  gxbLogisticsNo?: string;
  leChatTraceStatus?: LeChatTraceStatus;
  leChatTraceStatusOriginal?: string;
  erpTraceStatusOriginal?: string;
  lateDeliveryTimeMs?: Long;
  trackingNodes?: LogisticsNode[];
}

export interface ReverseShipment {
  externalReturnId?: string;
  trackingNumber?: string;
  shippingCarrier?: string;
  shipmentId?: string;
  leChatTraceStatus?: LeChatTraceStatus;
  leChatTraceStatusOriginal?: string;
  signedTimeMs?: Long;
  deliverySlaTimeMinMs?: Long;
  deliverySlaTimeMaxMs?: Long;
  trackingNodes?: LogisticsNode[];
}

export interface LogisticsNode {
  nodeTimeMs?: Long;
  description?: string;
  leChatTraceStatus?: LeChatTraceStatus;
  leChatTraceStatusOriginal?: string;
  attachments?: LogisticsAttachment[];
}

export interface LogisticsAttachment {
  filePath?: string;
  fileName?: string;
  url?: string;
  type?: string;
}

/**
 * 物流查询 V2。数组参数使用重复 query key（include=SHIPMENTS&include=TRACKING_NODES）。
 * 业务码不是 HTTP 状态码：200 成功，400 参数/平台不支持，403 权限不符，500 服务端异常。
 */
export function getLogisticsV2(params: GetLogisticsV2Query, signal?: GenericAbortSignal) {
  return doorClient
    .get<ApiResponse<GetLogisticsV2Data>>('/trade/v2/logistics', {
      params,
      paramsSerializer: (query) =>
        qs.stringify(query, {
          arrayFormat: 'none',
          skipNull: true,
          skipEmptyString: true,
        }),
      signal,
    })
    .then((res) => {
      const body = res.data;

      if (body?.code !== 200 || !body.data) {
        throw new Error(body?.msg || `Logistics V2 error: ${body?.code}`);
      }

      return body.data;
    });
}
